import React, { useEffect } from 'react';
import useModal from '@/hooks/useModal';
import { useFilters, useNotification } from '@/hooks';
import {
	CasesListType,
	NewConversationPayload,
	Subjects,
} from '@/interfaces/inbox';
import { ListCaseUsersComponent } from '@/components/modals/ListCaseUsersComponent';
import {
	ListCaseOptionsComponent,
	ListQuestionOptionsComponent,
	WriteNewEmail,
} from '@/components/modals';
import { FieldValues } from 'react-hook-form';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	GET_CASES_BY_CLIENT,
	GET_LAWYER_CLIENTS,
	GET_LAWYER_INBOX_LIST,
	GET_QUESTIONS_SUBJECTS,
} from '@/api';
import { inboxService } from '@/api/inbox';
import { Spinner } from '@/components/common';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';

interface NewEmailComponentProps {
	openNewEmail: boolean;
	setOpenNewMessage: (value: boolean) => void;
}

export const NewEmail: React.FC<NewEmailComponentProps> = ({
	openNewEmail,
	setOpenNewMessage,
}) => {
	const router = useRouter();
	const { removeParaFilter } = useFilters();
	const { clientId, caseId, applicationId } = router.query;
	const { toastSuccess, toastError } = useNotification();
	const [caseSelected, setCaseSelected] = React.useState<CasesListType>();
	const [questionSelected, setQuestionSelected] = React.useState<Subjects>();
	const [clientSelected, setClientSelected] = React.useState({
		id: '',
		name: '',
		last_name: '',
		profile_picture: '',
	});
	const queryClient = useQueryClient();
	const [isLoadingSendMessage, setIsLoadingSendMessage] =
		React.useState<boolean>(false);

	const [clients, subjects] = useQueries({
		queries: [
			{
				queryKey: [GET_LAWYER_CLIENTS],
				queryFn: () => inboxService.getLawyerClients(),
			},
			{
				queryKey: [GET_QUESTIONS_SUBJECTS],
				queryFn: () => inboxService.getInboxSubjects(),
			},
		],
	});

	const subjectCases = ['General Question', 'Account Settings'];

	const {
		data: cases,
		isLoading,
		isRefetching,
		refetch: refetchClients,
	} = useQuery({
		queryKey: [GET_CASES_BY_CLIENT, clientSelected, clientId],
		queryFn: () =>
			inboxService.getCasesByClient(clientSelected.id || (clientId as string)),
		enabled: !!clientSelected.id || !!clientId,
	});

	const listCases = cases?.data.items;
	const listClients =
		clients.data?.data.items.filter((client) => client.firstname) ?? [];
	const listSubjects = subjects.data?.data.items ?? [];

	const {
		Modal: ModalClients,
		show: showClients,
		hide: hideClients,
	} = useModal();

	const {
		Modal: ModalCaseInquired,
		show: showCaseInquired,
		hide: hideCaseInquired,
	} = useModal();

	const {
		Modal: ModalQuestion,
		show: showQuestion,
		hide: hideQuestion,
	} = useModal();

	const {
		Modal: ModalWriteNewEmail,
		show: showWriteNewEmail,
		hide: hideWriteNewEmail,
	} = useModal();

	useEffect(() => {
		// Si no está abierto, no hacemos nada
		if (!openNewEmail) return;

		if (applicationId && !caseSelected) {
			if (
				router.pathname.includes(AppRoutes.LAWYERAPP_CLIENT_PROFILE_ID) &&
				applicationId
			) {
				const findCase = listCases?.find(
					(caseItem) => caseItem.case.caseId === applicationId
				);
				if (findCase) {
					const objCase = {
						id: findCase.case.caseId,
						title: findCase.case.caseName,
						category: findCase.case.category.categoryName,
					};
					handleSelectCase(objCase);
				} else {
					showCaseInquired();
				}
			}
		}
		// Si no hay clientId ni caseId, mostramos la lista de clientes
		if (!clientId && !caseId && !clientSelected.id) {
			showClients();
			return;
		}

		// Si hay clientId pero no hay datos de clientes cargados, esperamos
		if (clientId && (!listClients || listClients.length === 0)) {
			return;
		}

		// Si hay clientId y datos de clientes, pero no hay cliente seleccionado
		if (
			(clientId && listClients.length > 0 && !clientSelected.id && caseId) ||
			(clientId && listClients.length > 0 && !clientSelected.id)
		) {
			const clientData = listClients.find((client) => client.id === clientId);
			if (clientData) {
				setClientSelected({
					id: clientData.id,
					name: clientData.firstname,
					last_name: clientData.lastname,
					profile_picture: clientData.profile_picture,
				});
				refetchClients();
				hideClients();
				// showCaseInquired();
			}
			if (!caseId && !applicationId) {
				showCaseInquired();
			}
		}
		// Si hay caseId pero no hay datos de casos cargados, esperamos
		if (caseId && (!listCases || listCases.length === 0)) {
			return;
		}

		// Si hay caseId y datos de casos, pero no hay caso seleccionado
		if (caseId && listCases && listCases.length > 0 && !caseSelected?.id) {
			const caseData = listCases.find(
				(infoCase) => infoCase.case.caseId === caseId
			);
			if (caseData) {
				const value: CasesListType = {
					id: caseData?.applicationId,
					category: caseData?.case.category.categoryName,
					title: caseData?.case.caseName,
				};
				handleSelectCase(value);
			}
		}
	}, [
		openNewEmail,
		clientId,
		caseId,
		listClients,
		listCases,
		clientSelected.id,
		caseSelected?.id,
		applicationId,
	]);

	const handleRefetch = async (): Promise<void> => {
		await queryClient.refetchQueries({
			queryKey: [GET_LAWYER_INBOX_LIST],
		});
	};

	const handleSelectClient = (
		id: string,
		name: string,
		last_name: string,
		profile_picture: string
	): void => {
		setClientSelected({
			id,
			name,
			last_name,
			profile_picture,
		});
		refetchClients();
		hideClients();
		showCaseInquired();
	};

	const handleSelectCase = (value: CasesListType): void => {
		const noCasesCategories = ['General', 'Account'];
		if (noCasesCategories.includes(value.category)) {
			setCaseSelected({
				...value,
				id: '',
			});
			showWriteNewEmail();
			setQuestionSelected({
				id: String(value.id),
				title:
					value.category === 'General'
						? 'General Question'
						: 'Account Settings',
				created_at: '',
				updated_at: '',
			});
		} else {
			setCaseSelected(value);
			showQuestion();
		}
		hideCaseInquired();
	};

	const handleSetQuestion = (value: Subjects): void => {
		setQuestionSelected(value);
		showWriteNewEmail();
		hideQuestion();
	};

	const handleSubmitNewEmail = async (data: FieldValues): Promise<void> => {
		setIsLoadingSendMessage(true);
		try {
			const newData: NewConversationPayload = {
				content: data.message,
			};
			if (clientSelected) {
				newData.clientId = clientSelected.id;
			}
			if (caseSelected && caseSelected.id) {
				newData.applicationId = Number(caseSelected.id);
			}
			if (questionSelected && questionSelected.id) {
				newData.subjectId = questionSelected.id;
			}

			toastSuccess('Your message was sent successfully.');
			await inboxService.postNewConversation(newData);
			handleRefetch();
			// removeParaFilter('clientId');
		} catch (error) {
			toastError('An error has occurred. Please try again');
		} finally {
			// hideWriteNewEmail();
			clearState();
			setIsLoadingSendMessage(false);
		}
	};

	const clearState = () => {
		//Query Param
		removeParaFilter('clientId');
		setOpenNewMessage(false);

		// State
		setCaseSelected(undefined);
		setQuestionSelected(undefined);
		setClientSelected({
			id: '',
			name: '',
			last_name: '',
			profile_picture: '',
		});

		//Modals
		hideClients();
		hideWriteNewEmail();
		hideCaseInquired();
		hideQuestion();
	};

	const isInitialLoading = clients.isLoading || subjects.isLoading;
	const isCasesLoading = isLoading || isRefetching;
	console.log({ listCases });
	// if (isInitialLoading || isCasesLoading) return <LoaderScreen />
	return (
		<>
			<ModalClients
				withXButton
				title="Who would you like to message?"
				classNameTitle="!text-base md:!text-xl mb-16 md:mb-16"
				backgroundColor="white"
				onClose={clearState}
				avoidClose
			>
				{isInitialLoading ? (
					<div className="flex justify-center">
						<Spinner />
					</div>
				) : (
					<ListCaseUsersComponent
						listClients={listClients}
						setClientSelected={handleSelectClient}
					/>
				)}
			</ModalClients>
			<ModalCaseInquired
				withXButton
				title="What would you like to message about?"
				classNameContainerChildren="lg:min-w-[300px] lg:min-h-[300px] lg:!max-h-[524px]"
				backgroundColor="white"
				onClose={clearState}
				avoidClose
			>
				{isCasesLoading ? (
					<div className="flex justify-center my-20">
						<Spinner />
					</div>
				) : (
					<ListCaseOptionsComponent
						cases={listCases}
						subjects={listSubjects.filter((subject) =>
							subjectCases.includes(subject.title)
						)}
						setCaseSelected={handleSelectCase}
					/>
				)}
			</ModalCaseInquired>
			<ModalQuestion
				withXButton
				title="What type of question are you asking about this case?"
				backgroundColor="white"
				onClose={clearState}
				avoidClose
				classNameContainerChildren="lg:!max-h-[560px]"
			>
				<ListQuestionOptionsComponent
					questionsSubjects={listSubjects.filter(
						(subject) => subjectCases[1] !== subject.title
					)}
					setQuestion={handleSetQuestion}
				/>
			</ModalQuestion>

			<ModalWriteNewEmail
				withXButton
				backgroundColor="white"
				onClose={clearState}
				avoidClose
			>
				{caseSelected && questionSelected && (
					<WriteNewEmail
						to={clientSelected.name}
						clientSelected={clientSelected}
						caseTitle={caseSelected?.title || ''}
						category={caseSelected?.category}
						subject={questionSelected}
						handleContentSubmit={handleSubmitNewEmail}
						isYourLawyer={false}
						isLoading={isLoadingSendMessage}
					/>
				)}
			</ModalWriteNewEmail>
		</>
	);
};
